import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Botao, Cartao, CartaoTopo, Vazio, Campo, Entrada, Selo } from "@notes/ui";
import { Video, ArrowLeft, Laptop, Trash2, Copy, ShieldCheck, Download, Chrome, Keyboard } from "lucide-react";
import { Pagina, Secao, Erro } from "../shell/Shell.js";
import { trpc, mensagemDeErro } from "../lib/api.js";
import { dataCurta } from "../lib/formato.js";

/**
 * GRAVAR SEM ROBÔ: a extensão do navegador.
 *
 * A tela existe para gerar o código do dispositivo. O código aparece UMA vez, e
 * a tela avisa isso ANTES de mostrar: quem descobre depois de fechar já perdeu.
 */
export function Gravar() {
  const qc = useQueryClient();
  const [apelido, setApelido] = useState("");
  const [novo, setNovo] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  const dispositivos = useQuery({ queryKey: ["extensao", "dispositivos"], queryFn: () => trpc.extensao.meusDispositivos.query() });
  const autorizar = useMutation({
    mutationFn: (a: string) => trpc.extensao.autorizarDispositivo.mutate({ apelido: a }),
    onSuccess: (r) => { setNovo(r.token); setApelido(""); void qc.invalidateQueries({ queryKey: ["extensao"] }); },
  });
  const revogar = useMutation({
    mutationFn: (id: string) => trpc.extensao.revogarDispositivo.mutate({ id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["extensao"] }),
  });

  const lista = dispositivos.data?.dispositivos ?? [];
  // A versão publicada vem do mesmo deploy que serve esta tela: o pacote que a
  // pessoa baixa fala com este servidor, e não com um de outra versão.
  const extensao = useQuery({
    queryKey: ["extensao", "versao"],
    queryFn: async () => (await fetch("/api/extensao/versao")).json() as Promise<{ versao: string | null; disponivel: boolean }>,
    staleTime: 5 * 60_000,
  });

  return (
    <Pagina
      titulo="Gravação e aparelhos"
      largura="estreita"
      descricao="A extensão do navegador grava a reunião sem entrar na sala como participante."
      acao={<Link to="/configuracoes"><Botao tamanho="sm" variante="fantasma"><ArrowLeft size={14} />Configurações</Botao></Link>}
    >
      <Secao ordem={0}>
        <Cartao>
          <CartaoTopo icone={<Chrome size={15} />} titulo="A extensão do Chrome"
            descricao="Sem robô entrando na sala: quem grava é o seu navegador, com a sua voz e a dos outros, e todo mundo vê o aviso."
            acao={extensao.data?.disponivel ? (
              <Botao tamanho="sm" variante="primario" onClick={() => window.location.assign("/api/extensao/download")}>
                <Download size={14} /> Baixar a extensão{extensao.data.versao ? ` v${extensao.data.versao}` : ""}
              </Botao>
            ) : <Selo tom="atencao">arquivo indisponível nesta versão</Selo>} />
          <ol className="enotes-passos">
            <li><strong>Baixe e descompacte</strong> o arquivo. Fica uma pasta chamada <code>enturos-notes-extensao</code>.</li>
            <li>No Chrome, abra <code>chrome://extensions</code>, ligue o <strong>Modo do desenvolvedor</strong> (canto superior direito) e clique em <strong>Carregar sem compactação</strong>, escolhendo a pasta.</li>
            <li>Fixe o ícone do Notes na barra (o alfinete no menu de extensões), gere o código abaixo e cole na extensão.</li>
            <li>Libere o microfone uma vez, na tela de opções da extensão. Sem ele, a sua voz não entra na gravação.</li>
            <li>Na reunião (Meet, Teams, Webex ou Zoom no navegador), aperte <kbd>Alt</kbd>+<kbd>Shift</kbd>+<kbd>N</kbd> ou clique no ícone e em Gravar. Marque destaque, nota, tarefa e dúvida durante a conversa; eles aparecem na reunião com o minuto certo.</li>
          </ol>
          <p className="enotes-rodape-nota"><Keyboard size={13} aria-hidden /> Atalhos: <kbd>Alt</kbd>+<kbd>Shift</kbd>+<kbd>N</kbd> grava ou para · <kbd>Alt</kbd>+<kbd>Shift</kbd>+<kbd>D</kbd> marca destaque. A gravação continua mesmo com a janelinha fechada, e a extensão avisa quando a reunião estiver pronta.</p>
        </Cartao>
      </Secao>

      {novo && (
        <Secao ordem={1}>
          <Cartao className="enotes-segredo">
            <CartaoTopo icone={<ShieldCheck size={15} />} titulo="Seu código, uma vez só"
              descricao="Guarde agora. Depois de fechar esta tela ele não pode mais ser mostrado, nem por quem administra a agência." />
            <code className="enotes-codigo">{novo}</code>
            <div className="enotes-acoes">
              <Botao variante="primario" onClick={() => { void navigator.clipboard.writeText(novo); setCopiado(true); }}>
                <Copy size={14} /> {copiado ? "Copiado" : "Copiar"}
              </Botao>
              <Botao variante="fantasma" onClick={() => { setNovo(null); setCopiado(false); }}>Já guardei</Botao>
            </div>
          </Cartao>
        </Secao>
      )}

      <Secao ordem={2}>
        <Cartao>
          <form className="enotes-form"
            onSubmit={(e) => { e.preventDefault(); if (apelido.trim().length >= 2) autorizar.mutate(apelido.trim()); }}>
            <Campo rotulo="Como você chama este computador" htmlFor="apelido"
              dica="O apelido é só para você reconhecer o aparelho nesta lista."
              erro={autorizar.isError ? mensagemDeErro(autorizar.error) : null}>
              <Entrada id="apelido" value={apelido} onChange={(e) => setApelido(e.target.value)}
                placeholder="Notebook do escritório" aria-label="Como você chama este computador" />
            </Campo>
            <div><Botao variante="primario" ocupado={autorizar.isPending}>Gerar código</Botao></div>
          </form>
        </Cartao>
      </Secao>

      <Secao titulo="Seus computadores" ordem={3}>
        {lista.length > 0 ? (
          <Cartao>
            <ul className="enotes-itens">
              {lista.map((d) => (
                <li key={d.id}>
                  <Laptop size={15} aria-hidden />
                  <span className="enotes-item-texto">{d.apelido}</span>
                  <span className="carimbo num">{d.prefixo}</span>
                  <span className="carimbo">
                    {d.ultimoUsoEm ? `usado em ${dataCurta(d.ultimoUsoEm)}` : "nunca usado"}
                  </span>
                  <Botao tamanho="sm" variante="fantasma" onClick={() => revogar.mutate(d.id)}>
                    <Trash2 size={13} /> Revogar
                  </Botao>
                </li>
              ))}
            </ul>
          </Cartao>
        ) : dispositivos.isSuccess && !novo ? (
          <Vazio titulo="Nenhum computador autorizado"
            descricao="Gere um código para ligar a extensão a esta conta." icone={<Laptop size={26} />} />
        ) : null}
      </Secao>
    </Pagina>
  );
}
